import React, {
  useEffect,
  useState,
  createContext,
  useCallback,
  useMemo,
} from "react";
import { CirromStyles } from "./components/CirromStyles";
import { Routes } from "./components/Routes";
import { ScrollToTop } from "./components/utils/ScrollToTop";
import { SnackbarProvider } from "notistack";
import { theme } from "./theme/theme";
import { ThemeProvider } from "@mui/material/styles";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { getCspNonce } from "./utils/cspNonce";
import { useRoutes } from "react-router-dom";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import {
  getActiveCrmSystemsForUser,
  IntegratedSystem,
} from "./api/integratedsystems";
import { DataProject, getDataProjectsForUser } from "./api/dataprojects";
import { SystemsAndProjectsProvider } from "./contexts/SystemsAndProjectsContext";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import "../src/App.css";
import { CirromLoadingLogo } from "./components/utils/CirromLoadingLogo";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { SyncIntegratedSystemsProvider } from "./hooks/useSyncIntegratedSystems";

ModuleRegistry.registerModules([AllCommunityModule]);

/**
 * Stands in for <StyledEngineProvider injectFirst>, which builds this exact cache
 * internally: key "css" keeps MUI's generated class names unchanged, and prepend is what
 * injectFirst does so plain CSS and CSS modules still win over MUI's own rules. Doing it
 * here is the only way to hand emotion a nonce, so its <style> tags can be allowed by a
 * `style-src 'nonce-...'` policy rather than needing 'unsafe-inline'. Built once, at
 * module scope: a cache rebuilt per render would re-inject every style.
 */
const emotionCache = createCache({
  key: "css",
  prepend: true,
  nonce: getCspNonce(),
});

/**
 * @typedef {Object} ViewPortContext
 * @property {string} value - The current value.
 * @property {Function} minimizeView - Function to minimize the view.
 * @property {Function} maximizeView - Function to maximize the view.
 */
export const ViewPortContext = createContext({
  value: "",
  minimizeView: () => {},
  maximizeView: () => {},
});

/**
 * Context for managing the width state of a sidebar.
 * @typedef {Object} SideBarWidthContext
 * @property {string} value - The current width value. Possible values: "full", "minimized", "maximized".
 * @property {function} toggleValue - Toggles the width value between "minimized" and "maximized".
 * @property {function} minimize - Sets the width value to "minimized".
 * @property {function} maximize - Sets the width value to "maximized".
 */
export const SideBarWidthContext = createContext({
  value: "full",
  toggleValue: () => {},
  minimize: () => {},
  maximize: () => {},
});

/**
 * Creates a context for the user profile.
 * @typedef {object} UserProfileContext
 * @property {React.Context} userProfile - The user profile context.
 * @property {boolean} canSupplyFeedback - Indicates whether the feedback can be supplied or not.
 */
export const UserProfileContext = createContext({
  userProfile: "USER",
  canSupplyFeedback: false,
  canUseCopilot: false,
});

/**
 * App entry point
 *
 */

function AppContent() {
  const auth = useAuth();
  const [userSystems, setUserSystems] = useState<IntegratedSystem[]>([]);
  const [dataProjects, setDataProjects] = useState<DataProject[]>([]);
  const [originalAccessPath, setOriginalAccessPath] =
    useState<string>(undefined);
  const [selectedSystemId, setSelectedSystemId] = useState<number>(undefined);

  const fetchDataProjects = useCallback(() => {
    getDataProjectsForUser()
      .then((projects: DataProject[]) => {
        setDataProjects(projects);
      })
      .catch((err) => console.log(err));
  }, []);

  // Store original access path on first load
  useEffect(() => {
    if (!originalAccessPath) {
      const originalPath = window.location.search
        ? (window.location.pathname + window.location.search).substring(1)
        : window.location.pathname;
      setOriginalAccessPath(originalPath);
    }
  }, [originalAccessPath]);

  const routing = useRoutes(
    Routes(
      auth.isLoggedIn,
      originalAccessPath,
      auth.userProfile,
      userSystems,
      dataProjects,
      fetchDataProjects,
      selectedSystemId,
      setSelectedSystemId
    )
  );

  useEffect(() => {
    if (auth.isLoggedIn) {
      getActiveCrmSystemsForUser()
        .then((systems) => {
          setUserSystems(systems);
        })
        .catch((err) => console.log(err));
      fetchDataProjects();
    }
  }, [auth.isLoggedIn, fetchDataProjects]);

  const [viewPortWidth, setViewPortWidth] = useState("");
  const [sideBarWidth, setSideBarWidth] = useState("full");

  // Use functional setState to avoid stale closure
  const toggleSidebarMemo = useCallback(() => {
    setSideBarWidth((prev) => (prev === "full" ? "small" : "full"));
  }, []);

  const minimizeSidebarMemo = useCallback(() => {
    setSideBarWidth("small");
  }, []);

  const maximizeSidebarMemo = useCallback(() => {
    setSideBarWidth("full");
  }, []);

  // Memoize ViewPortContext callbacks
  const minimizeView = useCallback(() => {
    setViewPortWidth("60%");
  }, []);

  const maximizeView = useCallback(() => {
    setViewPortWidth("");
  }, []);

  const viewPortParams = useMemo(
    () => ({
      value: viewPortWidth,
      minimizeView,
      maximizeView,
    }),
    [viewPortWidth, minimizeView, maximizeView]
  );

  const userProfileParams = useMemo(() => {
    return {
      userProfile: auth.userProfile,
      canSupplyFeedback: auth.canSupplyFeedback,
      canUseCopilot: auth.canUseCopilot,
    };
  }, [auth.userProfile, auth.canSupplyFeedback, auth.canUseCopilot]);

  const sideBarWidthParams = useMemo(() => {
    return {
      value: sideBarWidth,
      toggleValue: toggleSidebarMemo,
      minimize: minimizeSidebarMemo,
      maximize: maximizeSidebarMemo,
    };
  }, [
    sideBarWidth,
    toggleSidebarMemo,
    minimizeSidebarMemo,
    maximizeSidebarMemo,
  ]);

  return (
    <UserProfileContext.Provider value={userProfileParams}>
      <SideBarWidthContext.Provider value={sideBarWidthParams}>
        <ViewPortContext.Provider value={viewPortParams}>
          <CirromStyles />
          {auth.isLoading ? (
            <Box
              style={{
                backgroundColor: "background.default",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                justifyContent: "center",
              }}
            >
              <Container maxWidth="sm" style={{ padding: 0 }}>
                <div
                  style={{
                    justifyContent: "center",
                    display: "flex",
                    margin: "10px",
                  }}
                >
                  <CirromLoadingLogo iconHeight={"100px"} />
                </div>
              </Container>
            </Box>
          ) : (
            <SystemsAndProjectsProvider
              integratedSystems={userSystems}
              dataProjects={dataProjects}
              selectedSystemId={selectedSystemId}
              setSelectedSystemId={setSelectedSystemId}
              fetchDataProjects={fetchDataProjects}
            >
              <ScrollToTop>{routing}</ScrollToTop>
            </SystemsAndProjectsProvider>
          )}
        </ViewPortContext.Provider>
      </SideBarWidthContext.Provider>
    </UserProfileContext.Provider>
  );
}

function App() {
  return (
    <CacheProvider value={emotionCache}>
      <ThemeProvider theme={theme}>
        <SnackbarProvider maxSnack={3}>
          <AuthProvider>
            <SyncIntegratedSystemsProvider>
              <AppContent />
            </SyncIntegratedSystemsProvider>
          </AuthProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </CacheProvider>
  );
}

export default App;
